import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { HttpClient } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { tap, shareReplay, finalize } from 'rxjs/operators';
import { TenantService } from './tenant.service';
import { EventItem } from '../app/models/domain.models';

interface CacheEntry {
  data: EventItem[];
  timestamp: number;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})

export class EventService {
  private ongoingRequests = new Map<string, Observable<EventItem[]>>();
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds
  private readonly CACHE_PREFIX = 'event_cache_';

  constructor(
    private apiService: ApiService,
    private http: HttpClient,
    private tenantService: TenantService
  ) { }

  private getCacheKey(clientId: string): string {
    return `${this.CACHE_PREFIX}${clientId}`;
  }

  private getFromsessionStorage(clientId: string): CacheEntry | null {
    try {
      const key = this.getCacheKey(clientId);
      const cached = sessionStorage.getItem(key);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error('Error reading from sessionStorage:', error);
      return null;
    }
  }

  private setTosessionStorage(clientId: string, entry: CacheEntry): void {
    try {
      const key = this.getCacheKey(clientId);
      sessionStorage.setItem(key, JSON.stringify(entry));
    } catch (error) {
      console.error('Error writing to sessionStorage:', error);
    }
  }

  private removeFromsessionStorage(clientId: string): void {
    try {
      const key = this.getCacheKey(clientId);
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from sessionStorage:', error);
    }
  }

  getEvents(): Observable<EventItem[]> {
    const clientId = this.tenantService.getTenantId() || this.tenantService.getTenantKey();
    const cachedEntry = this.getFromsessionStorage(clientId);
    if (cachedEntry && Date.now() < cachedEntry.expiresAt) {
      return of(cachedEntry.data);
    }

    const ongoingRequest = this.ongoingRequests.get(clientId);
    if (ongoingRequest) {
      return ongoingRequest;
    }

    const request = this.apiService.getData<EventItem[]>('event/allEventDetails').pipe(
      tap(data => {
        // Cache the response in sessionStorage
        const now = Date.now();
        const cacheEntry: CacheEntry = {
          data: data,
          timestamp: now,
          expiresAt: now + this.CACHE_DURATION
        };
        this.setTosessionStorage(clientId, cacheEntry);
      }),
      finalize(() => this.ongoingRequests.delete(clientId)),
      shareReplay(1) // Share the same response with multiple subscribers
    );

    // Store the ongoing request
    this.ongoingRequests.set(clientId, request);

    return request;
  }

  // Method to clear cache for a specific clientId
  clearCache(clientId?: string): void {
    if (clientId) {
      this.removeFromsessionStorage(clientId);
    } else {
      // Clear all cache entries with the prefix
      try {
        const keys = Object.keys(sessionStorage);
        keys.forEach(key => {
          if (key.startsWith(this.CACHE_PREFIX)) {
            sessionStorage.removeItem(key);
          }
        });
      } catch (error) {
        console.error('Error clearing all cache:', error);
      }
    }
  }

  // Method to get cache status
  getCacheStatus(clientId: string): { hasCache: boolean; isExpired: boolean; expiresIn?: number } {
    const cachedEntry = this.getFromsessionStorage(clientId);
    if (!cachedEntry) {
      return { hasCache: false, isExpired: false };
    }

    const isExpired = Date.now() >= cachedEntry.expiresAt;
    const expiresIn = isExpired ? 0 : cachedEntry.expiresAt - Date.now();

    return {
      hasCache: true,
      isExpired,
      expiresIn
    };
  }

  // Method to check if there's an ongoing request
  hasOngoingRequest(clientId: string): boolean {
    return this.ongoingRequests.has(clientId);
  }
}
